import { useMemo } from 'react';

import {
  IAction,
  type IStep,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import CopyButton from '@/components/CopyButton';

import MessageActions from './Actions';
import CreateAlertButton from './CreateAlertButton';
import { DebugButton } from './DebugButton';
import { FeedbackButtons } from './FeedbackButtons';
import ScheduleReportButton from './ScheduleReportButton';

interface Props {
  message: IStep;
  actions: IAction[];
  run?: IStep;
  contentRef?: React.RefObject<HTMLDivElement>;
}

// The user question this answer replies to: the last user_message at or
// before the root containing the target step. The question is captured
// BEFORE the containment check so both tree shapes work — a user_message
// root followed by a separate run root, or an answer nested under the
// user_message root itself.
const findPrecedingUserQuestion = (
  roots: IStep[],
  targetId: string
): string => {
  const contains = (step: IStep): boolean =>
    step.id === targetId || (step.steps || []).some(contains);
  let question = '';
  for (const root of roots) {
    if (root.type === 'user_message' && root.output) question = root.output;
    if (contains(root)) return question;
  }
  return question;
};

const MessageButtons = ({ message, actions, run, contentRef }: Props) => {
  const { config } = useConfig();
  const { firstInteraction, messages } = useChatMessages();

  const isUser = message.type === 'user_message';
  const isAsk = message.waitForAnswer;
  const hasContent = !!message.output;
  const showCopyButton = !!run && hasContent && !isUser && !isAsk;

  const messageActions = actions.filter((a) => a.forId === message.id);

  const showDebugButton =
    !!config?.debugUrl && !!message.threadId && !!firstInteraction && !!run;

  const userQuestion = useMemo(
    () =>
      showCopyButton ? findPrecedingUserQuestion(messages, message.id) : '',
    [showCopyButton, messages, message.id]
  );

  const show = showCopyButton || showDebugButton || messageActions?.length;

  if (!show || message.streaming) {
    return null;
  }

  return (
    <div className="-ml-1.5 flex items-center flex-wrap">
      {showCopyButton ? (
        <CopyButton content={message.output} contentRef={contentRef} />
      ) : null}
      {showCopyButton ? <ScheduleReportButton question={userQuestion} /> : null}
      {showCopyButton ? <CreateAlertButton question={userQuestion} /> : null}
      {run ? <FeedbackButtons message={run} /> : null}
      {messageActions.length ? (
        <MessageActions actions={messageActions} />
      ) : null}
      {showDebugButton ? (
        <DebugButton debugUrl={config.debugUrl!} step={message} />
      ) : null}
    </div>
  );
};

export { MessageButtons };
